import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './store/hooks/reduxHooks';
import { restoreSession } from './store/slices/authSlice';
import { UserRole } from './types/auth';
import { setupInactivityTimer, setupBeforeUnloadHandler } from './utils/sessionManager';

// Pages
import { LoginPage } from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import ProfilePage from './pages/ProfilePage';
import ProductsPage from './pages/ProductTab/ManageProduct';
import ManageCategory from './pages/CategoryTab/ManageCategory';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { SuspendedPage } from './pages/SuspendedPage';
import ManageAdmins from './pages/AdminTab/ManageAdmins';
import ManagePartners from './pages/PartnerTab/ManagePartners';
import MerchantsPage from './pages/MerchantTab/ManageMerchants';
import ManageOrder from './pages/OrderTab/ManageOrder';
import ManageGatewayOrder from './pages/GatewayOrderTab/ManageGatewayOrder';
import ManageCancelOrder from './pages/CancelOrderTab/ManageCancelOrder';
import PreApprovalReportPage from './pages/Reports/PreApprovalReport';
import SettlementReportPage from './pages/Reports/SettlementReport';
import MerchantDataReportPage from './pages/Reports/MerchantDataReport';
import ManageBrands from './pages/PartnerBrandTab/ManageBrands';
import SchemesPage from './pages/SchemeTab/SchemesPage';
import ManageCleverPeSupply from './pages/CleverPeSupplyTab/ManageCleverPeSupply';
import ManageCustomers from './pages/CustomerTab/ManageCustomers';
import ManagePreApprovalLogs from './pages/PreApprovalLogsTab/ManagePreApprovalLogs';
import ManageGrievance from './pages/Grievance Tab/ManageGrievanceDetails';
import ExternalAPILogsPage from './pages/ExteranlLogsTab/ExternalLogsPage';
import Calculator from './pages/CalculatorTab/Calculator';
import SchemeBucket from './pages/schemeBucketTab/ManageBucket';
import KycPending from './pages/KycPendingTab/ManageMerchant'
import MerchantApproval from './pages/MerchantApprovalTab/LenderMerchantApproval';

// Components
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/layout/Layout';

// Lazy loaded components
const InvoicePage = lazy(() => import('./pages/Invoice/InvoicePage'));

// Add a new component to handle role-based redirection from the root path
const RootRedirect = () => {
  const { user, isAuthenticated } = useAppSelector(state => state.auth);
  
  console.log('[RootRedirect] User state:', {
    isAuthenticated,
    user: user ? {
      ...user,
    } : null
  });
  
  // If not authenticated, go to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is suspended
  if (user.suspended) {
    console.log('[RootRedirect] User is suspended');
    return <Navigate to="/suspended" replace />;
  }
  
  // Redirect based on user role
  switch (user.role) {
    case UserRole.ADMIN:
      return <Navigate to="/admin/dashboard" replace />;
    case UserRole.SUPERADMIN:
      return <Navigate to="/superadmin/dashboard" replace />;
    case UserRole.CLEVERPE_USER:
      return <Navigate to="/cleverpeuser/dashboard" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

const App: React.FC = () => {
  const dispatch = useAppDispatch();

  // Attempt to restore session on app initialization
  useEffect(() => {
    if (localStorage.getItem('token') && localStorage.getItem('user')) {
      console.log('Attempting to restore session from localStorage');
      dispatch(restoreSession());
    }
  }, [dispatch]);

  // Setup session management
  const { isAuthenticated } = useAppSelector(state => state.auth);
  
  useEffect(() => {
    // Clear any existing session management when auth state changes
    let cleanupInactivityTimer: ReturnType<typeof setupInactivityTimer> | null = null;
    let cleanupBeforeUnload: ReturnType<typeof setupBeforeUnloadHandler> | null = null;
    
    // Only set up session management if user is authenticated
    if (isAuthenticated) {
      console.log('Setting up session management');
      
      // Setup inactivity timer
      cleanupInactivityTimer = setupInactivityTimer(dispatch);
      
      // Setup beforeunload handler
      cleanupBeforeUnload = setupBeforeUnloadHandler(dispatch);
    }
    
    // Cleanup on unmount or when auth state changes
    return () => {
      if (cleanupInactivityTimer) cleanupInactivityTimer();
      if (cleanupBeforeUnload) cleanupBeforeUnload();
    };
  }, [dispatch, isAuthenticated]);

  return (
    <Router>
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/suspended" element={<SuspendedPage />} />
        
        {/* Profile routes for all roles */}
        <Route
          path="/admin/profile"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-dashboard"
            >
              <Layout>
                <ProfilePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/profile"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-dashboard"
            >
              <Layout>
                <ProfilePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/cleverpeuser/profile"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-dashboard"
            >
              <Layout>
                <ProfilePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Admin dashboard route - Only ADMIN role with dashboard permission */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-dashboard"
            >
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Superadmin dashboard route - Only SUPERADMIN role with dashboard permission */}
        <Route
          path="/superadmin/dashboard"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-dashboard" 
            >
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* CleverPe User dashboard route - Only CLEVERPE_USER role with dashboard permission */}
        <Route
          path="/cleverpeuser/dashboard"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-dashboard"
            >
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Admin Management routes - Requires manage-admins permission */}
        <Route
          path="/admin/manage-admins"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-manage-admin" 
            >
              <Layout>
                <ManageAdmins />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/manage-admins"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]} 
            >
              <Layout>
                <ManageAdmins />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Admin routes - Only ADMIN role */}
        {/* <Route
          path="/admin/manage-partner-orgs"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]} 
            >
              <Layout>
                <ManagePartners />
              </Layout>
            </ProtectedRoute>
          }
        /> */}
        
        <Route
          path="/admin/manage-partner"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-partner"
            >
              <Layout>
                <ManageBrands />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/manage-cleverpe-supply"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-cleverpe-supply+"
            >
              <Layout>
                <ManageCleverPeSupply />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/manage-merchant"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-merchant"
            >
              <Layout>
                <MerchantsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/manage-merchant-kyc-pending"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-merchant"
            >
              <Layout>
                <KycPending />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/manage-merchant-approval"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-merchant"
            >
              <Layout>
                <MerchantApproval />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/manage-customers"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-customer"
            >
              <Layout>
                <ManageCustomers />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/pre-approval-logs"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-store-approvals" 
            >
              <Layout>
                <ManagePreApprovalLogs />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/manage-scheme"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-scheme"
            >
              <Layout>
                <SchemesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/manage-product"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-product"
            >
              <Layout>
                <ProductsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/manage-category"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-category"
            >
              <Layout>
                <ManageCategory />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/manage-order"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-order"
            >
              <Layout>
                <ManageOrder />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/manage-gateway-order"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-gateway-order"
            >
              <Layout>
                <ManageGatewayOrder/>
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/manage-cancel-order"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-cancel-order"
            >
              <Layout>
                <ManageCancelOrder/>
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Invoice route - accessible to all authenticated users with orders permission */}
        <Route
          path="/invoice/:orderId"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN, UserRole.SUPERADMIN, UserRole.CLEVERPE_USER]}
              requiredPermission="view-order"
            >
              <Suspense fallback={<div className="flex justify-center items-center w-full h-screen">Loading...</div>}>
                <InvoicePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        
        {/* Report routes with report-specific permissions */}
        <Route
          path="/admin/reports-pre-approvals"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-pre-approvals-report"
            >
              <Layout>
                <PreApprovalReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/reports-settlements"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-settlements-report"
            >
              <Layout>
                <SettlementReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/reports-merchant-data"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-merchant-data-report"
            >
              <Layout>
                <MerchantDataReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/grievance"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-grievance-redressals"
            >
              <Layout>
                <ManageGrievance />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path = "/admin/external-logs"
          element={ 
            <ProtectedRoute
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-external-api-logs"
            >
              <Layout>
                <ExternalAPILogsPage />
              </Layout>
            </ProtectedRoute>
          }
        />  
        <Route
          path="/admin/calculator"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-calculator"
            >
              <Layout>
                <Calculator />
              </Layout>
            </ProtectedRoute>
          }
        />  
        <Route
          path="/admin/manage-scheme-bucket"
          element={
            <ProtectedRoute
              allowedRoles={[UserRole.ADMIN]}
              requiredPermission="view-scheme-bucket"
            >
              <Layout>
                <SchemeBucket />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Superadmin routes - Require appropriate permissions */}
        {/* <Route
          path="/superadmin/manage-partner-orgs"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-partner"
            >
              <Layout>
                <ManagePartners />
              </Layout>
            </ProtectedRoute>
          }
        /> */}
        
        <Route
          path="/superadmin/manage-partner"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-partner"
            >
              <Layout>
                <ManageBrands />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/manage-cleverpe-supply"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-cleverpe-supply+"
            >
              <Layout>
                <ManageCleverPeSupply />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/manage-merchant"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-merchant"
            >
              <Layout>
                <MerchantsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/superadmin/manage-merchant-kyc-pending"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-merchant"
            >
              <Layout>
                <KycPending />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/superadmin/manage-merchant-approval"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-merchant"
            >
              <Layout>
                <MerchantApproval />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/manage-customers"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-customer"
            >
              <Layout>
                <ManageCustomers />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/pre-approval-logs"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-store-approvals" 
            >
              <Layout>
                <ManagePreApprovalLogs />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/manage-scheme"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-scheme"
            >
              <Layout>
                <SchemesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/manage-product"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-product"
            >
              <Layout>
                <ProductsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/manage-category"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-category"
            >
              <Layout>
                <ManageCategory />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/manage-order"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-order"
            >
              <Layout>
                <ManageOrder />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/manage-cancel-order"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-cancel-order"
            >
              <Layout>
                <ManageCancelOrder/>
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/superadmin/manage-gateway-order"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-gateway-order"
            >
              <Layout>
                <ManageGatewayOrder />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/reports-pre-approvals"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-pre-approvals-report"
            >
              <Layout>
                <PreApprovalReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/reports-settlements"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-settlements-report"
            >
              <Layout>
                <SettlementReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/superadmin/reports-merchant-data"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-merchant-data-report"
            >
              <Layout>
                <MerchantDataReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/grievance"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-grievance-redressals"
            >
              <Layout>
                <ManageGrievance />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path = "/superadmin/external-logs"
          element={
            <ProtectedRoute
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-external-api-logs"
            >
              <Layout>
                <ExternalAPILogsPage />
              </Layout>
            </ProtectedRoute>
          }
        />  
        <Route
          path="/superadmin/calculator"
          element={
            <ProtectedRoute
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-calculator"
            >
              <Layout>
                <Calculator />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/manage-scheme-bucket"
          element={
            <ProtectedRoute
              allowedRoles={[UserRole.SUPERADMIN]}
              requiredPermission="view-scheme-bucket"
            >
              <Layout>
                <SchemeBucket />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* CleverPe User routes with required permissions */}
        <Route
          path="/cleverpeuser/products"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-product"
            >
              <Layout>
                <ProductsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cleverpeuser/categories"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-category"
            >
              <Layout>
                <ManageCategory />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* <Route
          path="/cleverpeuser/partner-orgs"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-partner"
            >
              <Layout>
                <ManagePartners />
              </Layout>
            </ProtectedRoute>
          }
        /> */}
        
        <Route
          path="/cleverpeuser/partners"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-partner"
            >
              <Layout>
                <ManageBrands />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/cleverpeuser/manage-cleverpe-supply"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-cleverpe-supply+"
            >
              <Layout>
                <ManageCleverPeSupply />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/cleverpeuser/merchants"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-merchant"
            >
              <Layout>
                <MerchantsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/cleverpeuser/customers"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-customer"
            >
              <Layout>
                <ManageCustomers />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/cleverpeuser/pre-approval-logs"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-store-approvals"
            >
              <Layout>
                <ManagePreApprovalLogs />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/cleverpeuser/schemes"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-scheme"
            >
              <Layout>
                <SchemesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/cleverpeuser/orders"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-order"
            >
              <Layout>
                <ManageOrder />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/cleverpeuser/gateway-orders"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-gateway-order"
            >
              <Layout>
                <ManageGatewayOrder />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/cleverpeuser/reports-pre-approvals"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-pre-approvals-report"
            >
              <Layout>
                <PreApprovalReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/cleverpeuser/reports-settlements"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-settlements-report"
            >
              <Layout>
                <SettlementReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/cleverpeuser/reports-merchant-data"
          element={
            <ProtectedRoute 
              allowedRoles={[UserRole.CLEVERPE_USER]}
              requiredPermission="view-merchant-data-report"
            >
              <Layout>
                <MerchantDataReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Redirect routes */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/superadmin" element={<Navigate to="/superadmin/dashboard" replace />} />
        <Route path="/cleverpeuser" element={<Navigate to="/cleverpeuser/dashboard" replace />} />
        
        {/* Root route redirects based on role */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/dashboard" element={<RootRedirect />} />
        <Route path="*" element={<Navigate to="/unauthorized" replace />} />
        </Routes>
    </Router>
  );
};

export default App; 